import { DATE_PRESETS, FUTURE_DATE_PRESETS } from "../constants";
import {
  DATE_TERMS,
  createDateFilterValue,
  getFilterIndicator,
  tryParseDate,
} from "../helpers";
import type { FilterTypeConfig, SearchResult } from "../types";
import { format } from "date-fns";

import type { FilterStrategy, SearchContext } from "./types";

/**
 * Shared search step for `date` and `birthday` strategies — both react to the
 * same parsed-date / preset queries; only the emitted toggle format differs.
 */
export function searchDateLike(
  filterType: FilterTypeConfig,
  ctx: SearchContext,
  out: SearchResult[],
): void {
  const parsedDate = tryParseDate(ctx.query);
  if (parsedDate) {
    const formattedDate = format(parsedDate, "EEE, MMM d, yyyy");
    out.push({
      filterKey: filterType.filterKey,
      filterLabel: filterType.label,
      optionValue: `__specific_date__`,
      optionLabel: formattedDate,
      type: filterType.type,
      specificDate: parsedDate,
      score: 100,
      indicator: getFilterIndicator(filterType),
    });
  }

  const labelLower = filterType.label.toLowerCase();
  const matchesDateTerm =
    labelLower.includes(ctx.query) ||
    DATE_TERMS.some(
      (term) => term.includes(ctx.query) || ctx.query.includes(term),
    );

  if (!matchesDateTerm) return;
  const presets =
    filterType.direction === "future" ? FUTURE_DATE_PRESETS : DATE_PRESETS;
  for (const preset of presets) {
    if (
      preset.label.toLowerCase().includes(ctx.query) ||
      labelLower.includes(ctx.query)
    ) {
      out.push({
        filterKey: filterType.filterKey,
        filterLabel: filterType.label,
        optionValue: `__date_preset_${preset.days}__`,
        optionLabel: preset.label,
        type: filterType.type,
        datePreset: { days: preset.days },
        score: 60,
        indicator: getFilterIndicator(filterType),
      });
    }
  }
}

export const dateStrategy: FilterStrategy = {
  search: searchDateLike,
  isSelected(
    currentValue,
    _optionValue,
    _comparison,
    _numericValue,
    datePreset,
    filterConfig,
  ) {
    if (
      Array.isArray(currentValue) &&
      currentValue.length === 2 &&
      datePreset !== undefined
    ) {
      const expectedValue = createDateFilterValue(
        datePreset.days,
        filterConfig.direction === "future" ? "future" : "past",
      );
      return (
        currentValue[0] === expectedValue[0] &&
        currentValue[1] === expectedValue[1]
      );
    }
    return false;
  },
  toggle(result, _currentValue, filterConfig) {
    if (result.specificDate) {
      const dateStr = format(result.specificDate, "yyyy-MM-dd");
      return { value: [dateStr, dateStr] };
    }
    if (result.datePreset) {
      // "Next 30 days" on a future-facing filter, not the last 30.
      return {
        value: createDateFilterValue(
          result.datePreset.days,
          filterConfig.direction === "future" ? "future" : "past",
        ),
      };
    }
    return { value: result.optionValue };
  },
};
